/**
 * Portal Runtime Configuration
 * 
 * Fetches configuration from /config.json at startup.
 * This allows the same Docker image to be used across environments
 * (staging, production) with different configuration values.
 * 
 * The fetch starts immediately when this module is imported,
 * so by the time you call getConfig(), it's likely already resolved.
 */

export interface PortalConfig {
  /** Keycloak server URL (default: /auth) */
  keycloakUrl: string;
  /** Keycloak realm (default: master) */
  keycloakRealm: string;
  /** Keycloak client ID (default: bloomstack-app) */
  keycloakClientId: string;
  /** When set, shows a banner at the top of the app (e.g. "Staging — data may be reset at any time") */
  stagingBanner?: string;
  /** When true, the TTS (ttsen) feature is enabled. Any non-empty env value counts as enabled. */
  ttsEnabled?: boolean;
  /** Preview domain for HTML file previews (e.g. "*.bloomstack.io"). Empty string = previews disabled. */
  previewDomain?: string;
}

/** Default configuration values */
const defaultConfig: PortalConfig = {
  keycloakUrl: '/auth',
  keycloakRealm: 'master',
  keycloakClientId: 'bloomstack-app',
};

/**
 * Fetch starts IMMEDIATELY when this module loads.
 * This runs in parallel with other JS parsing/execution.
 */
const configPromise: Promise<PortalConfig> = fetch('/config.json')
  .then(response => {
    if (!response.ok) {
      console.warn(`Failed to load /config.json (${response.status}), using defaults`);
      return defaultConfig;
    }
    return response.json();
  })
  .then(config => {
    const merged = { ...defaultConfig, ...config };
    // Any non-empty value means enabled; coerce config.json string/bool to boolean.
    merged.ttsEnabled = !!merged.ttsEnabled;
    // Allow dev-time override via .env.local (VITE_STAGING_BANNER, VITE_TTSEN_ENABLED)
    const envBanner = import.meta.env?.VITE_STAGING_BANNER;
    if (envBanner) merged.stagingBanner = envBanner;
    const envTts = import.meta.env?.VITE_TTSEN_ENABLED;
    if (envTts) merged.ttsEnabled = true;
    return merged;
  })
  .catch(error => {
    console.warn('Failed to load /config.json, using defaults:', error);
    return defaultConfig;
  });

/** Cached config after first await */
let cachedConfig: PortalConfig | null = null;

/**
 * Get the portal configuration.
 * Returns a promise that resolves to the config.
 * 
 * Since the fetch started when the module loaded, this is
 * typically already resolved by the time you call it.
 */
export async function getConfig(): Promise<PortalConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }
  cachedConfig = await configPromise;
  return cachedConfig;
}

/**
 * Get the portal configuration synchronously.
 * Only use this AFTER awaiting getConfig() at least once.
 * Throws if config hasn't been loaded yet.
 */
export function getConfigSync(): PortalConfig {
  if (!cachedConfig) {
    throw new Error('Config not loaded yet. Call await getConfig() first.');
  }
  return cachedConfig;
}
